import CancelIcon from "@mui/icons-material/Cancel"
import DeleteIcon from "@mui/icons-material/Delete"
import SaveIcon from "@mui/icons-material/Save"
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
} from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"
import { getAnalytics, logEvent } from "firebase/analytics"
import { getFirestore } from "firebase/firestore"
import { FunctionComponent, useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import Zoom from "react-medium-image-zoom"
import "react-medium-image-zoom/dist/styles.css"
import Color from "../Color"
import Dimen from "../Dimen"
import { AmountType, StorageKey } from "../Enum"
import { timber_debug } from "../Timber"
import { Account, Record, RecordType } from "../Type"
import { getRecordType, getRecordTypeByName } from "../Utils"
import {
  getFileIdByName,
  getFolderIdByName,
  getImageBlobUrl,
} from "../contexts/GDriveContext"
import { createRecord, deleteRecord, editRecord } from "../daos/RecordDAO"
import { formatCurrencyAmount } from "../utils/CurrencyUtils"
import { DATE_FORMAT } from "../utils/DateUtils"
import { isEmptyString } from "../utils/TextUtils"

const RecordDialog: FunctionComponent<{
  userId: string
  record: Record
  recordTypes: RecordType[]
  selectedAccount: Account
  onClickClose: () => void
}> = ({ userId, record, recordTypes, onClickClose, selectedAccount }) => {
  const intl = useIntl()

  // Firebase
  const firestore = getFirestore()
  const analytics = getAnalytics()

  // Record Dialog states
  const [recId, setRecId] = useState<string | undefined>(record.id)
  const [recDate, setRecDate] = useState<number>(record.date)
  const [recAmount, setRecAmount] = useState<number | string>(record.amount)
  const [recCredit, setRecCredit] = useState<AmountType>(
    record.credit ? AmountType.Credit : AmountType.Debit
  )

  // For new record, set the first recTypes array element as the recordType
  const [recType, setRecType] = useState<RecordType>(
    getRecordType(record, recordTypes)
  )
  const [recNote, setRecNote] = useState<string>(record.note)
  const [recInsertDate, setRecInsertDate] = useState<number>(record.insertDate)
  const [imageURL, setImageURL] = useState<string>("")
  const [snackBarMsg, setSnackBarMsg] = useState<string>("")

  useEffect(() => {
    setRecId(record.id)
    setRecDate(record.date)
    setRecAmount(formatCurrencyAmount(record.amount))
    setRecCredit(record.credit ? AmountType.Credit : AmountType.Debit)
    setRecType(getRecordType(record, recordTypes))
    setRecNote(record.note)
    setRecInsertDate(record.insertDate)
    return () => {}
  }, [record])

  /**
   * Download receipt from GDrive if exists
   */
  useEffect(() => {
    if (!record.invoiceName || record.invoiceName === "") return
    const accessToken = localStorage.getItem(StorageKey.AccessToken)
    if (!accessToken || accessToken === "") return

    /**
     * Load receipt image from GDrive
     */
    const loadReceipt = async () => {
      const folderId = await getFolderIdByName(accessToken, "Invoices")
      if (!folderId || folderId === "") {
        // Record has receipt, but GDrive access code expired!
        setSnackBarMsg("Sign in again to view receipt image")
        return
      }

      const fileId = await getFileIdByName(
        accessToken,
        folderId,
        record.invoiceName
      )
      if (!fileId || fileId === "") return

      const imageURL = await getImageBlobUrl(accessToken, fileId)
      if (!imageURL || imageURL === "") return
      setImageURL(imageURL)
    }

    loadReceipt()
    return () => {}
  }, [record])

  const saveRecord = () => {
    timber_debug(
      `Rec id: ${recId} date: ${recDate} amount: ${recAmount} credit: ${recCredit} typeId: ${recType.id} note: ${recNote} accountId: ${selectedAccount?.id}`
    )
    if (isEmptyString(recId)) {
      logEvent(analytics, "web_create_record_clicked")
      // Create new record
      createRecord(
        firestore,
        userId,
        recDate,
        recAmount,
        recCredit,
        recType,
        recNote,
        record.invoiceName,
        record.invoiceUri,
        selectedAccount,
        onClickClose,
        analytics
      )
    } else {
      // Edit existing record
      logEvent(analytics, "web_edit_record_clicked")
      editRecord(
        firestore,
        userId,
        recId,
        recDate,
        recAmount,
        recCredit,
        recType,
        recNote,
        record.invoiceName,
        record.invoiceUri,
        selectedAccount,
        recInsertDate,
        onClickClose,
        analytics
      )
    }
  }

  return (
    <div>
      <DialogTitle>
        {record.id === "" ? (
          <FormattedMessage id="dialog.title.create" />
        ) : (
          <FormattedMessage id="dialog.title.edit" />
        )}
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={1}
          direction={"row"}
          alignItems="center"
          rowSpacing={5}
          marginTop={1}
        >
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            {/* Record date */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={intl.formatMessage({
                  id: "table.headers.date",
                })}
                value={dayjs(recDate)}
                onChange={(newDate) => {
                  logEvent(analytics, "web_record_date_selected")
                  setRecDate(dayjs(newDate).valueOf())
                }}
                format={DATE_FORMAT}
              />
            </LocalizationProvider>
          </Grid>

          {/* Record amount */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <TextField
              fullWidth
              required
              type="number"
              label={intl.formatMessage({
                id: "table.headers.amount",
              })}
              onChange={(newValue: any) => {
                if (newValue.target.value !== "") {
                  setRecAmount(parseFloat(newValue.target.value))
                } else {
                  setRecAmount("")
                }
              }}
              value={recAmount}
            />
          </Grid>

          {/* Credit/Debit checkbox */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <FormLabel>
              <FormattedMessage id="dialog.credit.debit" />
            </FormLabel>
            <RadioGroup
              row
              value={recCredit}
              onChange={(event) => {
                setRecCredit(event.target.value as AmountType)
              }}
            >
              <FormControlLabel
                value="credit"
                control={<Radio />}
                label={intl.formatMessage({
                  id: "dialog.credit",
                })}
              />
              <FormControlLabel
                value="debit"
                control={<Radio />}
                label={intl.formatMessage({
                  id: "dialog.debit",
                })}
              />
            </RadioGroup>
          </Grid>

          {/* Record type  */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <TextField
              fullWidth
              select
              label={intl.formatMessage({
                id: "table.headers.type",
              })}
              value={recType.name}
              onChange={(event: any) => {
                const recType = getRecordTypeByName(
                  event.target.value,
                  recordTypes
                )
                logEvent(analytics, "web_rec_type_selected")
                setRecType(recType)
              }}
            >
              {recordTypes.map((recType, index) => (
                <MenuItem key={index} value={recType.name}>
                  {recType.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Note */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <TextField
              fullWidth
              multiline
              label={intl.formatMessage({
                id: "table.headers.note.optional",
              })}
              value={recNote}
              onChange={(event: any) => {
                setRecNote(event.target.value)
              }}
            />
          </Grid>
        </Grid>

        {/* Receipt image */}
        {imageURL !== "" && (
          <Zoom>
            <img
              style={{ marginTop: Dimen.MarginDefault }}
              src={imageURL}
              alt={"Receipt"}
              loading="lazy"
              width={"20%"}
              height={"20%"}
            />
          </Zoom>
        )}
      </DialogContent>

      <Snackbar
        open={!isEmptyString(snackBarMsg)}
        autoHideDuration={4000}
        onClose={() => {
          setSnackBarMsg("")
        }}
      >
        <Alert
          variant="filled"
          severity="warning"
          sx={{
            width: "100%",
            background: Color.PrimaryLight85,
            color: Color.Black,
          }}
        >
          {snackBarMsg}
        </Alert>
      </Snackbar>

      <DialogActions>
        {/* Cancel button */}
        <Button
          onClick={() => {
            setImageURL("")
            onClickClose()
          }}
          variant="outlined"
          startIcon={<CancelIcon />}
        >
          <FormattedMessage id="dialog.button.cancel" />
        </Button>

        {/* Delete button */}
        {recId !== "" && (
          <Button
            color="error"
            onClick={() => {
              logEvent(analytics, "web_delete_rec_clicked")
              deleteRecord(firestore, userId, recId, onClickClose, analytics)
            }}
            variant="outlined"
            startIcon={<DeleteIcon />}
          >
            <FormattedMessage id="dialog.button.delete" />
          </Button>
        )}

        {/* Save button */}
        <Button
          onClick={saveRecord}
          variant="contained"
          style={{ background: Color.Primary }}
          startIcon={<SaveIcon />}
        >
          <FormattedMessage id="dialog.button.save" />
        </Button>
      </DialogActions>
    </div>
  )
}

export default RecordDialog
